import React from "react";

const domains = [
  [
    "1",
    "Beliefs",
    "Mindset",
    "What the mind has become stuck on about self, others, situations, and systems; victim narrative, negative self-talk, comparison, acting out of character",
  ],
  [
    "2",
    "Belonging",
    "Community",
    "All relationships and whether the respondent is truly seen inside them; doing everything alone, unspoken expectations, resentment",
  ],
  [
    "3",
    "Boundaries",
    "Triggers",
    "What is in place to protect the respondent's mental health, time, and goals; people-pleasing, the guilty yes, enmeshment with one's roles",
  ],
  [
    "4",
    "Business",
    "Purpose",
    "Calling and passion, whether or not it ever becomes a formal business; disconnection from purpose, the deferred dream",
  ],
  [
    "5",
    "Budget",
    "Finances",
    "Money read as information about one's life rather than punishment; paycheck-to-paycheck stress, money conflict, comfort spending",
  ],
  [
    "6",
    "Body",
    "Health",
    "Rest, eating, movement, and self-care; the tired that sleep does not fix, physical signals ignored",
  ],
];

const MethodologyConcept: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        3. The conceptual model: six B's
      </h2>
      <p className="mb-3">
        The instrument is built on Ieshia Danielle's Becoming Methodology, a
        coaching framework developed over roughly a year of practice with
        Christian women, first in a small pilot intensive and then at a live
        event in Mississippi. The framework holds that the exhaustion its
        clients describe as burnout is not a single thing with a single source.
        It is sustained by whichever of six life domains is most out of order,
        and the domains are addressed in a fixed teaching order because earlier
        domains tend to feed later ones.
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-xs md:text-sm border border-[#E8DEC8] rounded-lg overflow-hidden">
          <thead className="bg-[#4A0E26] text-white">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Order</th>
              <th className="px-3 py-2 text-left font-semibold">Domain</th>
              <th className="px-3 py-2 text-left font-semibold">
                Framework Label
              </th>
              <th className="px-3 py-2 text-left font-semibold">
                What the Domain Covers
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8DEC8]">
            {domains.map(([order, domain, label, covers]) => (
              <tr key={order}>
                <td className="px-3 py-2">{order}</td>
                <td className="px-3 py-2 font-semibold">{domain}</td>
                <td className="px-3 py-2">{label}</td>
                <td className="px-3 py-2">{covers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mb-3">
        Three modeling decisions follow from the framework and are stated here
        as design commitments rather than findings.
      </p>
      <p className="mb-3">
        The domains are treated as exhaustive for this population. The framework
        author reviewed the patterns arriving in her coaching inbox, including
        from pregnant women and women in ministry roles, and concluded that no
        seventh domain was needed. This is an assumption, not a result, and
        Section 7 lists it as one.
      </p>
      <p className="mb-3">
        The domains are not treated as mutually exclusive. A respondent's
        burnout can be, and in the framework's language usually is, braided from
        more than one domain. The scoring therefore reports a ranked profile
        rather than a single label, and it has an explicit blend rule (Section
        5.3).
      </p>
      <p>
        Mapping decisions are fixed. Some statements could plausibly belong to
        more than one domain. The framework resolves these deliberately: "I
        don't know who I am outside of my roles" is scored as Boundaries
        (enmeshment), not Beliefs. Doing everything oneself with building
        resentment is scored as Belonging. Low self-esteem language is scored as
        Beliefs when the trigger is inner narrative and as Body when the trigger
        is self-image and upkeep. These rules are documented so that any future
        revision of item wording preserves the same mapping.
      </p>
      <p className="mt-3">
        The instrument does not measure the MBI's or the BAT's dimensions and
        does not claim to. Its relationship to those instruments is
        complementary: they answer "how burned out, and in what form," and this
        instrument answers "from which part of life." A respondent who wants a
        validated measure of burnout severity should use one of the instruments
        cited in Section 2.
      </p>
    </section>
  </div>
);

export default MethodologyConcept;
